import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FormRef } from '@lib/components/ReactHookForm/types';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useTaskFields from 'routes/Task/utils/useFields/useTaskFields';
import useModalManagement from '@lib/hooks/useModalManagement';
import TaskAdd from 'components/Task/TaskAdd';
import SiteAreasModalTableMultiselect, {
  ModalTableMultiselectMode as SiteAreasModalTableMultiselectMode,
} from 'components/SiteAreasModalTableMultiselect/SiteAreasModalTableMultiselect';
import AssetsModalTableMultiselect, {
  ModalTableMultiselectMode as AssetsModalTableMultiselectMode,
} from 'components/AssetsModalTableMultiselect/AssetsModalTableMultiselect';
import useActions from './utils/useActions';
import useSearchListQueries from './utils/useSearchListQueries';
import {
  modals,
  selectAreaModal,
  unselectAreaModal,
  selectAssetModal,
  unselectAssetModal,
} from './modals';

interface Props {
  isDuplicate?: boolean;
}

export default function TaskCreateContainer({ isDuplicate }: Props) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const { pathname } = useLocation();
  const useModalManagementResult = useModalManagement(modals);

  const {
    createMutationError,
    createTaskLoading,
    fullTaskData,
    fullTaskError,
    fullTaskLoading,
    onAreasMultiselect,
    onAssetsMultiselect,
    onCloseAreasModal,
    onCloseAssetsModal,
    onCreate,
    onCreateNewFormTemplate,
    onViewFormTemplate,
    openAreasModalTableMultiselect,
    openAreasModalTableMultiunselect,
    openAssetsModalTableMultiselect,
    openAssetsModalTableMultiunselect,
  } = useActions({
    formRef,
    useModalManagementResult,
  });

  const {
    getApproversForCreateFormLoadOptions: getApproversLoadOptions,
    getAssetsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    getTemplateVersionsLoadOptions,
    getTemplatesLoadOptions,
  } = useSearchListQueries();

  const formFields = useTaskFields({
    getApproversLoadOptions,
    getAssetsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    getTemplatesLoadOptions,
    getTemplateVersionsLoadOptions,
    isCreate: true,
    isCreateForm: true,
    onCreateNewFormTemplate,
    onViewFormTemplate,
    openAreasModalTableMultiselect,
    openAssetsModalTableMultiselect,
    openAreasModalTableMultiunselect,
    openAssetsModalTableMultiunselect,
    t,
  });

  const isAreasModalOpen =
    useModalManagementResult.isModalOpened(selectAreaModal) ||
    useModalManagementResult.isModalOpened(unselectAreaModal);
  const isAssetsModalOpen =
    useModalManagementResult.isModalOpened(selectAssetModal) ||
    useModalManagementResult.isModalOpened(unselectAssetModal);

  const areasModalMode = useModalManagementResult.isModalOpened(
    unselectAreaModal,
  )
    ? SiteAreasModalTableMultiselectMode.SelectedOnly
    : SiteAreasModalTableMultiselectMode.WithTabs;
  const assetsModalMode = useModalManagementResult.isModalOpened(
    unselectAssetModal,
  )
    ? AssetsModalTableMultiselectMode.SelectedOnly
    : AssetsModalTableMultiselectMode.WithTabs;

  return (
    <>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({ pathname, t, entity: fullTaskData })}
      />
      <TaskAdd
        createMutationError={createMutationError}
        createTaskLoading={createTaskLoading}
        formFields={formFields}
        formRef={formRef}
        fullTaskData={fullTaskData}
        fullTaskError={fullTaskError}
        fullTaskLoading={fullTaskLoading}
        isDuplicate={isDuplicate}
        onSubmit={onCreate}
      />
      <SiteAreasModalTableMultiselect
        isOpen={isAreasModalOpen}
        onClose={onCloseAreasModal}
        onSelect={onAreasMultiselect}
        formRef={formRef}
        mode={areasModalMode}
      />
      <AssetsModalTableMultiselect
        isOpen={isAssetsModalOpen}
        onClose={onCloseAssetsModal}
        onSelect={onAssetsMultiselect}
        formRef={formRef}
        mode={assetsModalMode}
      />
    </>
  );
}
